// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductInventoryDto,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import { xIsAuthenticated } from "@tps/App/Store/Selectors/GeneralSelectors";
import IconStockAvailable from "@tps/XDL/Icons/IconStockAvailable";
import IconStockRecommended from "@tps/XDL/Icons/IconStockRecommended";
import IconStockUnavailable from "@tps/XDL/Icons/IconStockUnavailable";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-Change
    qtyOnHand?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // Xng-Change
    isAuthenticated: xIsAuthenticated(state),
    signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentLocation: getLocation(state),
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-Change
    contactUsMessage?: TypographyPresentationProps;
    messageWrapper?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        size: 12,
        css: css`
            letter-spacing: 0;
            line-height: 17px;
        `,
    },
    contactUsMessage: {
        size: 12,
        lineHeight: "17px",
    },
    messageWrapper: {
        css: css`
            display: flex;
        `,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    font-size: 12px;
    letter-spacing: 0;
    line-height: 17px;
    padding: 0;
    ${IconWrapper} {
        margin-right: 4px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return IconStockUnavailable;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return IconStockRecommended;
    }

    return IconStockAvailable;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-Change
    isAuthenticated,
    signInPageUrl,
    currentLocation,
    qtyOnHand,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (!showInventory || (!isAuthenticated && !isProductDetailsPage)) {
        return null;
    }

    if (!isAuthenticated && isProductDetailsPage) {
        const returnUrl = currentLocation.pathname + currentLocation.search;
        return (
            <StyledWrapper {...styles.container}>
                <Link
                    // xng-change
                    // href={`${signInPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`}
                    href={`${signInPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}&isForceRedirect=true`}
                    {...styles.availabilityByWarehouseLink}
                    aria-label="sign in for availability"
                >
                    {siteMessage("Availability_SignInForAvailability")}
                </Link>
            </StyledWrapper>
        );
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                {/* contact us for availability */}
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    if (isProductDetailsPage) {
        return (
            <StyledWrapper {...styles.messageWrapper}>
                <Typography {...styles.contactUsMessage}>
                    {qtyOnHand && qtyOnHand > 0
                        ? `${translate("Currently In Stock")}`
                        : translate("Contact Us for Availability")}
                </Typography>
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        inventoryTextComponent = (
            <InventoryMessage color="text.main" {...styles.inventoryMessage}>
                <Icon size={12} src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availability.message}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {/* XNG-Change: will not display the modal and link */}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
